import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { compensationKeys } from './queryKeys';

export interface AssignmentHistoryEntry {
  id: string;
  profile_id: string;
  action: 'created' | 'updated' | 'assigned' | 'unassigned' | 'deactivated' | 'bulk_assigned';
  rule_name: string;
  rule_type: string;
  performed_by: string | null;
  performed_at: string;
  old_values: Record<string, any>;
  new_values: Record<string, any>;
  reason: string | null;
  performer_name?: string;
  performer_avatar?: string;
  employee_name?: string;
  employee_avatar?: string;
}

export const useAssignmentHistory = (employeeId?: string, maxEntries = 10) => {
  return useQuery({
    queryKey: [...compensationKeys.all, 'assignment-history', employeeId, maxEntries],
    queryFn: async () => {
      if (!employeeId) return [];

      let query = supabase
        .from('compensation_assignment_history')
        .select(`
          id,
          profile_id,
          action,
          rule_name,
          rule_type,
          performed_by,
          performed_at,
          old_values,
          new_values,
          reason,
          performer:profiles!performed_by(
            first_name,
            last_name
          ),
          employee:profiles!profile_id(
            first_name,
            last_name
          )
        `)
        .eq('profile_id', employeeId)
        .order('performed_at', { ascending: false })
        .limit(maxEntries);

      const { data, error } = await query;

      if (error) {
        console.error('Error fetching assignment history:', error);
        throw error;
      }

      // Transform the data to include formatted names
      const transformedData: AssignmentHistoryEntry[] = (data || []).map((entry: any) => ({
        id: entry.id,
        profile_id: entry.profile_id,
        action: entry.action,
        rule_name: entry.rule_name,
        rule_type: entry.rule_type,
        performed_by: entry.performed_by,
        performed_at: entry.performed_at,
        old_values: entry.old_values || {},
        new_values: entry.new_values || {},
        reason: entry.reason,
        performer_name: entry.performer 
          ? `${entry.performer.first_name} ${entry.performer.last_name}` 
          : 'System',
        performer_avatar: entry.performer?.avatar_url,
        employee_name: entry.employee 
          ? `${entry.employee.first_name} ${entry.employee.last_name}` 
          : 'Unknown Employee',
        employee_avatar: entry.employee?.avatar_url
      }));

      return transformedData;
    },
    enabled: !!employeeId,
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000, // 10 minutes
  });
};